import { addMonths, isAfter, startOfMonth } from 'date-fns'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import {
  CUSTOM_MODULE,
  EQUITY_CASH_OUT,
  HOME_ESTIMATE,
  HOME_ESTIMATE_EMPTY_STATE,
  HOME_INSURANCE,
  IMPORTANT_NOW,
  MORTGAGE_INSURANCE,
  PRINCIPAL_INTEREST,
  PURCHASING_POWER,
  REFINANCE_OPTIONS,
  RENTAL_POTENTIAL,
  REVERSE_MORTGAGE
} from '../../../constants/document_ids'
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import { selectIsFirstDigestView } from '../../../store/selectors/clientEvents'
import {
  selectShouldAttemptRenderRecommendations,
  selectShouldRenderCashout,
  selectShouldRenderHomeInsuranceModule,
  selectShouldRenderMarketPanel,
  selectShouldRenderPurchasingPower,
  selectShouldRenderRefiOptions
} from '../../../store/selectors/display'
import { selectPaidInFull } from '../../../store/selectors/home/answers'
import { selectFirstCEL } from '../../../store/selectors/home/loans'
import { selectHomeValue } from '../../../store/selectors/home/metadata'
import { selectShowReverseMortgage } from '../../../store/selectors/home/reverseMortgage'
import { selectShouldShowAirDnaModule } from '../../../store/selectors/airDna'
import { RootState } from '../../../types/rootState'
import { HomeownerDigestTrackingEventLocation } from '../../../constants/tracking'
import { NavigationShortcutDatum } from '../../../types/navigation'
import useViewport from '../../../hooks/useViewport'
import { useMemo } from 'react'
import { selectShowPmiProvidedModule } from '../../../store/slices/pmi'
import { Icon } from '@homebotapp/hb-react-component-catalog'
import { selectCustomModules } from '../../../store/selectors/customerProfile'

const tileNames = {
  HOME_ESTIMATE: 'Home Estimate',
  YOUR_MARKET: 'Your Market',
  IMPORTANT_NOW: 'Important Now',
  CUSTOM_MODULE: 'From Your Advisor',
  PRINCIPAL_INTEREST: 'Principal & Interest',
  MORTGAGE_INSURANCE: 'Mortgage Insurance',
  HOME_INSURANCE: 'Home Insurance',
  REVERSE_MORTGAGE: 'Reverse Mortgage',
  REFINANCE_OPTIONS: 'Refinance Options',
  PURCHASING_POWER: 'Purchasing Power',
  EQUITY_CASH_OUT: 'Equity Cash Out',
  SHORT_TERM_RENTAL: 'Short-term Rentals'
} as const

export const MSG = defineMessages({
  homeEstimate: {
    id: 'HomeownerNavigationLinksOptions.homeEstimate',
    defaultMessage: 'Home Estimate'
  },
  yourMarket: {
    id: 'HomeownerNavigationLinksOptions.yourMarket',
    defaultMessage: 'Your Market'
  },
  customModule: {
    id: 'HomeownerNavigationLinksOptions.customModule',
    defaultMessage: 'From Your Advisor'
  },
  ImportantNow: {
    id: 'HomeownerNavigationLinksOptions.importantNow',
    defaultMessage: 'Important Now'
  },
  principalInterest: {
    id: 'HomeownerNavigationLinksOptions.principalInterest',
    defaultMessage: 'Principal & Interest'
  },
  mortgageInsurance: {
    id: 'HomeownerNavigationLinksOptions.mortgageInsurance',
    defaultMessage: 'Mortgage Insurance'
  },
  homeInsurance: {
    id: 'HomeownerNavigationLinksOptions.homeInsurance',
    defaultMessage: 'Home Insurance'
  },
  reverseMortgage: {
    id: 'HomeownerNavigationLinksOptions.reverseMortgage',
    defaultMessage: 'Reverse Mortgage'
  },
  refinanceOptions: {
    id: 'HomeownerNavigationLinksOptions.refinanceOptions',
    defaultMessage: 'Refinance Options'
  },
  purchasingPower: {
    id: 'HomeownerNavigationLinksOptions.purchasingPower',
    defaultMessage: 'Purchasing Power'
  },
  equityCashOut: {
    id: 'HomeownerNavigationLinksOptions.equityCashOut',
    defaultMessage: 'Equity Cash Out'
  },
  shortTermRental: {
    id: 'HomeownerNavigationLinksOptions.shortTermRental',
    defaultMessage: 'Short-term Rentals'
  }
})

export const useJumpToOptions = () => {
  const intl = useIntl()

  const homeValue = useSelector<RootState, number>(selectHomeValue)
  const showMarketSpotlight = useSelector(selectShouldRenderMarketPanel)
  const showWin = useSelector(selectShouldAttemptRenderRecommendations)
  const paidInFull = useSelector(selectPaidInFull)
  const loanOriginationDate = useSelector(selectFirstCEL)?.date?.split('-').join('/')
  const firstPaymentDate = addMonths(startOfMonth(new Date(loanOriginationDate)), 2)
  const hasValidLoan = isAfter(new Date(), firstPaymentDate) && !paidInFull
  const showPmiProvidedModule = useSelector(selectShowPmiProvidedModule)
  const shouldRenderHomeInsurance = useSelector(selectShouldRenderHomeInsuranceModule)
  const showReverseMortgage = useSelector(selectShowReverseMortgage)
  const showAirDnaModule = useSelector(selectShouldShowAirDnaModule)
  const hasRefiOptions = useSelector(selectShouldRenderRefiOptions)
  const showPurchasingPower = useSelector(selectShouldRenderPurchasingPower)
  const showCashOut = useSelector(selectShouldRenderCashout)

  const customModules = useSelector(selectCustomModules)
  const ownCustomModule = customModules?.find((module: any) => module.moduleType === 'own')
  const showCustomModule = ownCustomModule ?? false

  const iconColor = 'brand.700'
  const iconWidth = '3'
  const iconHeight = '3'

  const { isLargeOrAbove } = useViewport()

  const trackingUIContext = isLargeOrAbove
    ? HomeownerDigestTrackingEventLocation.HOMEOWNER_DIGEST_SIDE_NAViGATION_PANEL
    : HomeownerDigestTrackingEventLocation.HOMEOWNER_DIGEST_MOBILE_SHORTCUTS_DRAWER_SELECT_COMPONENT

  const jumpToOptions: NavigationShortcutDatum[] = [
    {
      // if no homeValue, show empty state
      name: tileNames.HOME_ESTIMATE,
      icon: <Icon name='chart-line' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.homeEstimate),
      href: homeValue ? `#${HOME_ESTIMATE}` : `#${HOME_ESTIMATE_EMPTY_STATE}`,
      tracking: {
        guid: 'sfxzvBmkfKxuHdvCSvTLT9',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.HOME_ESTIMATE}`
      }
    },
    {
      // showMarketSpotlight
      name: tileNames.YOUR_MARKET,
      icon: <Icon name='map-marker-mdc' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.yourMarket),
      href: OVERLAY_HELPERS.marketSpotlight.buildPath(),
      tracking: {
        guid: 'wrDs6zje9c6m216VDEho3w',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.YOUR_MARKET}`
      }
    },
    {
      // custom module
      name: tileNames.CUSTOM_MODULE,
      icon: <Icon name='star-bts' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.customModule),
      href: `#${CUSTOM_MODULE}`,
      tracking: {
        guid: 'wrDs6zje9c6m216VDEho3w',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.CUSTOM_MODULE}`
      }
    },
    {
      // win array length present and not first digest
      name: tileNames.IMPORTANT_NOW,
      icon: <Icon name='bolt' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.ImportantNow),
      href: `#${IMPORTANT_NOW}`,
      tracking: {
        guid: '4mui3YsNGz5cSo7Xx3FAp2',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.IMPORTANT_NOW}`
      }
    },
    {
      // hasValidLoan
      name: tileNames.PRINCIPAL_INTEREST,
      icon: <Icon name='no-data' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.principalInterest),
      href: `#${PRINCIPAL_INTEREST}`,
      tracking: {
        guid: 'jkiR3e3ZUFJX1TAspCrPsL',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.PRINCIPAL_INTEREST}`
      }
    },
    {
      // pmi
      name: tileNames.MORTGAGE_INSURANCE,
      icon: <Icon name='emergency' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.mortgageInsurance),
      href: `#${MORTGAGE_INSURANCE}`,
      tracking: {
        guid: 'cvofx2io9jEGs3gSaSHCXR',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.MORTGAGE_INSURANCE}`
      }
    },
    {
      // homeInsurance
      name: tileNames.HOME_INSURANCE,
      icon: <Icon name='buoy' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.homeInsurance),
      href: `#${HOME_INSURANCE}`,
      tracking: {
        guid: '3fWfsxcKhB4sa8UdtDwLQv',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.HOME_INSURANCE}`
      }
    },
    {
      // reverse mortgage
      name: tileNames.REVERSE_MORTGAGE,
      icon: <Icon name='refresh' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.reverseMortgage),
      href: `#${REVERSE_MORTGAGE}`,
      tracking: {
        guid: '4A2PZbP2A6YbmAVBPH16Yy',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.REVERSE_MORTGAGE}`
      }
    },
    {
      // has valid loan & refi options present
      name: tileNames.REFINANCE_OPTIONS,
      icon: <Icon name='refi' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.refinanceOptions),
      href: `#${REFINANCE_OPTIONS}`,
      tracking: {
        guid: '7eTyafbAzvCWx9qy1diFc3',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.REFINANCE_OPTIONS}`
      }
    },
    {
      // refi eligible and equity above cashout minimum
      name: tileNames.PURCHASING_POWER,
      icon: <Icon name='wallet' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.purchasingPower),
      href: `#${PURCHASING_POWER}`,
      tracking: {
        guid: 'eUncn5upWgx8noLVmW7Rm7',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.PURCHASING_POWER}`
      }
    },
    {
      // when present
      name: tileNames.EQUITY_CASH_OUT,
      icon: <Icon name='sellpocket' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.equityCashOut),
      href: `#${EQUITY_CASH_OUT}`,
      tracking: {
        guid: 'vytzaRYcJcuVTWtLAEGchG',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.EQUITY_CASH_OUT}`
      }
    },
    {
      // when present
      name: tileNames.SHORT_TERM_RENTAL,
      icon: <Icon name='rentalhome' color={iconColor} width={iconWidth} height={iconHeight} />,
      body: intl.formatMessage(MSG.shortTermRental),
      href: `#${RENTAL_POTENTIAL}`,
      tracking: {
        guid: '74sjNnAvz2kJDnFWwphArr',
        ui_context: trackingUIContext,
        descriptor: `${tileNames.SHORT_TERM_RENTAL}`
      }
    }
  ]

  const filteredJumpToOptions = useMemo(
    () =>
      jumpToOptions.filter(tile => {
        if (tile.name === tileNames.YOUR_MARKET) {
          if (!showMarketSpotlight || !homeValue) {
            return false
          }
        }
        if (tile.name === tileNames.CUSTOM_MODULE && !showCustomModule) return false
        if (tile.name === tileNames.IMPORTANT_NOW && !showWin) return false
        if (tile.name === tileNames.PRINCIPAL_INTEREST && !hasValidLoan) return false
        if (tile.name === tileNames.MORTGAGE_INSURANCE && !showPmiProvidedModule) return false
        if (tile.name === tileNames.HOME_INSURANCE && !shouldRenderHomeInsurance) return false
        if (tile.name === tileNames.REVERSE_MORTGAGE && !showReverseMortgage) return false
        if (tile.name === tileNames.SHORT_TERM_RENTAL && !showAirDnaModule) return false
        if (
          (tile.name === tileNames.REFINANCE_OPTIONS && !hasValidLoan) ||
          (tile.name === tileNames.REFINANCE_OPTIONS && !hasRefiOptions)
        )
          return false
        if (tile.name === tileNames.PURCHASING_POWER && !showPurchasingPower) return false
        if (tile.name === tileNames.EQUITY_CASH_OUT && !showCashOut) return false
        return true
      }),
    [
      showMarketSpotlight,
      showWin,
      hasValidLoan,
      showPmiProvidedModule,
      shouldRenderHomeInsurance,
      showReverseMortgage,
      showAirDnaModule,
      hasRefiOptions,
      showPurchasingPower,
      showCashOut
    ]
  )

  return filteredJumpToOptions
}
